/**
 * Pure access decision for the missed-appointment recovery queue route
 * (`/dashboard/coc/no-shows`) -- FRONT-DESK-DAY-BOARD-V1. Mirrors
 * decideFollowupQueueAccess verbatim in shape: wait while loading; redirect
 * to /login on error or no scope; otherwise render when EITHER admission
 * arm holds:
 *
 *  - the server-computed `missedAppointmentQueueEnabled` /me door signal is
 *    strictly true (the #294 door pattern) -- the backend computes it from
 *    the org's Q-05=D audience config with the SAME admission predicate the
 *    queue route enforces, so this guard carries NO role knowledge of its
 *    own; or
 *  - the caller holds the 'coc' nav section -- the EXACT pre-package guard
 *    (RequireNavSection section="coc") preserved byte-for-byte, so a /me
 *    served without the new signal (an older backend mid-deploy) never
 *    locks the existing audience out (fail-same).
 *
 * Backend route auth (the queue's config-audience gate + clinic fences)
 * remains the real security boundary; this guard is UX cleanliness only,
 * exactly like its siblings.
 */

import type { DashboardMe } from '@/dashboard/types';

export type NoShowQueueAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

export function decideNoShowQueueAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): NoShowQueueAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (
    state.me.missedAppointmentQueueEnabled === true ||
    state.me.navSections.includes('coc')
  ) {
    return { kind: 'render' };
  }
  return { kind: 'redirect', to: state.me.homePath };
}
